import { parseEntityRef } from '@backstage/catalog-model';
import type { AgentToolCallTrace } from '../../api/MgaApi';

interface AgentDocSource {
  title: string;
  entityRef: string;
  path?: string;
}

export interface AgentCitationGroups {
  entityRefs: string[];
  docs: AgentDocSource[];
  failedToolCalls: AgentToolCallTrace[];
}

const ENTITY_REF_KEYS = [
  'entityRef',
  'entity_ref',
  'entity',
  'ref',
  'targetRef',
];

const DOC_PATH_KEYS = ['path', 'location', 'page', 'docPath'];
const DOC_TITLE_KEYS = ['title', 'name'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function isValidEntityRef(entityRef: string): boolean {
  if (!entityRef.includes(':')) {
    return false;
  }

  try {
    parseEntityRef(entityRef);
    return true;
  } catch {
    return false;
  }
}

function findStringByKeys(value: unknown, keys: string[]): string | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  for (const key of keys) {
    const candidate = value[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  for (const nestedValue of Object.values(value)) {
    if (Array.isArray(nestedValue)) {
      for (const item of nestedValue) {
        const result = findStringByKeys(item, keys);
        if (result) return result;
      }
    } else if (isRecord(nestedValue)) {
      const result = findStringByKeys(nestedValue, keys);
      if (result) return result;
    }
  }

  return undefined;
}

function findAllEntityRefs(value: unknown): string[] {
  if (!isRecord(value)) {
    return [];
  }

  const matches: string[] = [];
  for (const [key, nestedValue] of Object.entries(value)) {
    if (
      ENTITY_REF_KEYS.includes(key) &&
      typeof nestedValue === 'string' &&
      isValidEntityRef(nestedValue)
    ) {
      matches.push(nestedValue);
    } else if (Array.isArray(nestedValue)) {
      nestedValue.forEach(item => matches.push(...findAllEntityRefs(item)));
    } else if (isRecord(nestedValue)) {
      matches.push(...findAllEntityRefs(nestedValue));
    }
  }

  return matches;
}

function toDocSource(toolCall: AgentToolCallTrace): AgentDocSource | undefined {
  const toolName = toolCall.toolName.toLowerCase();
  const args = toolCall.args;
  const entityRef = findStringByKeys(args, ENTITY_REF_KEYS);

  if (
    !entityRef ||
    !isValidEntityRef(entityRef) ||
    !toolName.includes('techdocs')
  ) {
    return undefined;
  }

  const path = findStringByKeys(args, DOC_PATH_KEYS);
  const title = findStringByKeys(args, DOC_TITLE_KEYS) || path || entityRef;

  return {
    title,
    entityRef,
    path,
  };
}

export function getAgentCitationGroups(
  toolCalls: AgentToolCallTrace[],
): AgentCitationGroups {
  const failedToolCalls = toolCalls.filter(toolCall => toolCall.isError);
  const docs = toolCalls
    .map(toDocSource)
    .filter((doc): doc is AgentDocSource => Boolean(doc));
  const docEntityRefs = new Set(docs.map(doc => doc.entityRef));
  const entityRefs = unique(
    toolCalls
      .flatMap(toolCall => findAllEntityRefs(toolCall.args))
      .filter(entityRef => !docEntityRefs.has(entityRef)),
  );

  return {
    entityRefs,
    docs,
    failedToolCalls,
  };
}
